import { forwardRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Done,
  MoreVert,
  RadioButtonUnchecked,
  VBaseSelect,
  VFlex,
  VIconButton,
  useMergedRef,
  useVBaseSelectContext,
} from '@/package';
import { usePcdCacheStore } from '@/store';
import type { Task } from '@/type';

const CacheStatusIcon = ({
  loadedPcdCount,
  totalPcdCount,
}: {
  loadedPcdCount: number;
  totalPcdCount: number;
}) => {
  if (!loadedPcdCount) return <Done className="size-4 text-gray-300" />;
  if (loadedPcdCount === totalPcdCount)
    return <Done className="size-4 text-secondary-500" />;

  return <RadioButtonUnchecked className="size-4 text-success-500" />;
};

export const PcdCacheStatus = ({ task }: { task: Task }) => {
  const { t } = useTranslation();
  const { removePcdList } = usePcdCacheStore();
  const { getPcdStatusMap, getPcdPathMap: getPcdPathList } = usePcdCacheStore();
  const [loadedPcdCount, setLoadedPcdCount] = useState(0);
  const [pcdPathMap, setPcdPathMap] = useState<Dictionary<string>>({});
  const totalPcdCount = task.fileFrameCount;

  const getTaskFullPath = () => {
    return `${task.basePath}${task.basePath ? '/' : ''}${task.orgPath}`
      .replace(/^\//, '')
      .replace(/\/+/g, '/');
  };

  useEffect(() => {
    getPcdPathList(getTaskFullPath()).then(setPcdPathMap);
  }, [getPcdPathList, task.basePath, task.orgPath]);

  const getLoadedPcdCount = () => {
    getPcdStatusMap(getTaskFullPath()).then(res => {
      const pcdStatusList = Object.entries(res);
      const loadedPcdCount = pcdStatusList.filter(
        ([_, status]) => status.loaded,
      ).length;
      setLoadedPcdCount(loadedPcdCount);
    });
  };

  useEffect(() => {
    getLoadedPcdCount();
  }, [task]);

  return (
    <VFlex gap={0.25} items="center">
      <CacheStatusIcon
        loadedPcdCount={loadedPcdCount}
        totalPcdCount={totalPcdCount}
      />
      <span className="text-body-sm text-gray-500">
        {t('collection.cached.camphocarboxylic')}
      </span>
      <span className="text-body-sm font-semibold text-gray-500">
        {loadedPcdCount}
      </span>
      <span className="text-body-sm text-gray-500">/{totalPcdCount}</span>

      <VBaseSelect
        itemList={[{ label: 'Delete cache', value: 'clear' }]}
        renderItem={({ item }) => (
          <div className="text-body-lg text-black">{item.label}</div>
        )}
        onItemClick={e => {
          if (e._target.item.value === 'clear') {
            removePcdList(
              Object.values(pcdPathMap).map(p => `${getTaskFullPath()}/${p}`),
            ).then(() => getLoadedPcdCount());
          }
        }}
      >
        <InnerPcdCacheStatus />
      </VBaseSelect>
    </VFlex>
  );
};

const InnerPcdCacheStatus = forwardRef((_, ref) => {
  const { $displayValue } = useVBaseSelectContext();
  const refs = useMergedRef($displayValue, ref);

  return (
    <VIconButton ref={refs} Icon={MoreVert} size="sm" variant="roundPrimary" />
  );
});
